import { AniPayFormData } from 'app/pages/anipay/model/types';
import i18n from 'app/utils/translation/i18n';
import { array, object, string } from 'yup';
import { formatNumber } from '../alias/add-alias-modal/add-alias-modal';

export const AniPaySchema = object<AniPayFormData>().shape({
  bankCode: string().notRequired(),
  aliasType: object({
    value: string().required(() => i18n.t('anipay.schema_aliasType')),
    label: string().required(() => i18n.t('anipay.schema_aliasType')),
  }).required(() => i18n.t('anipay.schema_aliasType')),
  aliasValue: string()
    .when('aliasType.value', {
      is: 'IBAN',
      then: schema => schema.notRequired(),
      otherwise: schema => schema.required(() => i18n.t('anipay.schema_aliasValue')),
    })
    .when('aliasType.value', {
      is: 'MOBILE',
      then: schema =>
        schema
          .required(() => i18n.t('anipay.schema_phoneNumber_required'))
          .test(
            'valid-phone',
            () => i18n.t('anipay.schema_phoneNumber_min_9_symbols'),
            val => {
              return (formatNumber(val)?.length ?? 0) >= 9;
            }
          ),
    })
    .when('aliasType.value', {
      is: 'EMAIL',
      then: schema =>
        schema
          .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, () =>
            i18n.t('anipay.schema_wrong_email_format')
          )
          .min(5, () => i18n.t('anipay.schema_email_min_5_symbols'))
          .max(50, () => i18n.t('anipay.schema_email_max_50_symbols'))
          .required(() => i18n.t('anipay.schema_email_required')),
    })
    .when('aliasType.value', {
      is: 'TIN',
      then: schema =>
        schema
          .matches(/^[0-9]+$/, () => i18n.t('anipay.schema_tin_only_numbers'))
          .length(10, () => i18n.t('anipay.schema_tin_min_10_symbols'))
          .required(() => i18n.t('anipay.schema_tin_required')),
    })
    .when('aliasType.value', {
      is: 'PIN',
      then: schema =>
        schema
          .transform(value => value?.toUpperCase())
          .matches(/^[A-Z0-9]+$/, () => i18n.t('anipay.schema_only_uppercase_english_letters'))
          .min(5, () => i18n.t('anipay.schema_pin_min_5_symbols'))
          .max(7, () => i18n.t('anipay.schema_pin_max_7_symbols'))
          .required(() => i18n.t('anipay.schema_pin_required')),
    }),
  iban: string().when('aliasType.value', {
    is: 'IBAN',
    then: schema =>
      schema
        .transform(value => value?.toUpperCase())
        .matches(/^AZ[0-9]{2}[A-Z0-9]{24}$/, () => i18n.t('anipay.schema_iban_invalid_format'))
        .length(28, () => i18n.t('anipay.schema_iban_min_28_symbols'))
        .required(() => i18n.t('anipay.schema_iban_required')),
    otherwise: schema => schema.notRequired(),
  }),
  clientType: object({
    value: string().when('aliasType.value', {
      is: 'IBAN',
      then: schema => schema.required(() => i18n.t('anipay.schema_clientType_required')),
      otherwise: schema => schema.notRequired(),
    }),
    label: string().when('aliasType.value', {
      is: 'IBAN',
      then: schema => schema.required(() => i18n.t('anipay.schema_clientType_required')),
      otherwise: schema => schema.notRequired(),
    }),
  }).when('aliasType.value', {
    is: 'IBAN',
    then: schema => schema.notRequired(),
    otherwise: schema => schema.required(() => i18n.t('anipay.schema_clientType_required')),
  }),
  beneficiaryName: string().when('aliasType.value', {
    is: 'IBAN',
    then: schema =>
      schema.when('clientType.value', {
        is: (value: string) => !!value,
        then: s => s.required(() => i18n.t('anipay.schema_beneficiaryName_required')),
        otherwise: s => s.notRequired(),
      }),
    otherwise: schema => schema.notRequired(),
  }),
  beneficiaryTIN: string().when('aliasType.value', {
    is: 'IBAN',
    then: schema =>
      schema.when('clientType.value', {
        is: (value: string) => !!value && value !== '3',
        then: s =>
          s
            .required(() => i18n.t('anipay.schema_beneficiaryTIN_required'))
            .length(10, () => i18n.t('anipay.schema_beneficiaryTIN_length_10')),
        otherwise: s => s.notRequired(),
      }),
    otherwise: schema => schema.notRequired(),
  }),

  beneficiaryCode: string().when('aliasType.value', {
    is: 'IBAN',
    then: schema => schema,
    otherwise: schema => schema.notRequired(),
  }),
  beneficiaryBankName: string().when('aliasType.value', {
    is: 'IBAN',
    then: schema => schema,
    otherwise: schema => schema.notRequired(),
  }),
  beneficiaryAccountName: string().when('aliasType.value', {
    is: 'IBAN',
    then: schema => schema,
    otherwise: schema => schema.notRequired(),
  }),
  bank_name: string(),
  cr_acc_no: string(),
  cr_name: string(),
  cr_tax_no: string(),
  cr_type_id: string().notRequired(),
  bank_account: string(),
  contract_no: string()
    .required(() => i18n.t('anipay.schema_contract_account_number_required'))
    .matches(/^(?! )[a-zA-Z0-9\s]*$/, () =>
      i18n.t('anipay.schema_contract_account_number_only_english_letters_and_numbers')
    ),
  description: string()
    .required(() => i18n.t('anipay.schema_payment_assignment_required'))
    .matches(/^[a-zA-Z0-9əöğçşüƏÖĞÇŞÜıI\s.,-]*$/, () =>
      i18n.t('anipay.schema_only_az_eng_letters')
    ),
  amount: string()
    .matches(/^(\d+\.?\d*|\.\d+)$/, () => i18n.t('anipay.schema_only_numbers'))
    .required(() => i18n.t('anipay.schema_amount_required')),
  account: object({
    value: string().required(() => i18n.t('anipay.schema_outflow_account_required')),
    label: string().required(() => i18n.t('anipay.schema_outflow_account_required')),
  }).required(() => i18n.t('anipay.schema_outflow_account_required')),
  comm_account: object({
    value: string().required(() => i18n.t('anipay.schema_commission_outflow_account_required')),
    label: string().required(() => i18n.t('anipay.schema_commission_outflow_account_required')),
  }).required(() => i18n.t('anipay.schema_commission_outflow_account_required')),
  files: array()
    .of(
      object({
        file_id: string(),
        name: string(),
        size: string(),
      })
    )
    .when('clientType.value', {
      is: (value: string) => value === '3',
      then: schema => schema.required(() => i18n.t('anipay.schema_file_is_not_selected')),
    }),
});
